/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useEffect, useState } from "react";
import {
  type MatrixClient,
  KnownMembership,
  type Room,
  RoomStateEvent,
} from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";

export interface KnockRequest {
  userId: string;
  name: string;
  avatarMxcUrl: string | null;
}

export interface KnockRequests {
  requests: KnockRequest[];
  /** Lets the user in. Absent if we may not invite people to the room. */
  admit: ((userId: string) => void) | undefined;
  /** Turns the user away. Absent if we may not kick people from the room. */
  decline: ((userId: string) => void) | undefined;
}

/**
 * The people currently asking to join a room, and what we can do about them.
 * Only someone with the power to invite ever gets to act on a request.
 */
export function useKnockRequests(
  client: MatrixClient,
  room: Room,
): KnockRequests {
  const [snapshot, setSnapshot] = useState(() => readRequests(room));

  useEffect(() => {
    const update = (): void => setSnapshot(readRequests(room));
    update();
    const state = room.currentState;
    state.on(RoomStateEvent.Update, update);
    state.on(RoomStateEvent.Members, update);
    return (): void => {
      state.off(RoomStateEvent.Update, update);
      state.off(RoomStateEvent.Members, update);
    };
  }, [room]);

  const myUserId = client.getUserId();
  const admit = useCallback(
    (userId: string): void => {
      client.invite(room.roomId, userId).catch((e) => {
        logger.error(`Failed to admit ${userId} into ${room.roomId}`, e);
      });
    },
    [client, room],
  );
  const decline = useCallback(
    (userId: string): void => {
      client.kick(room.roomId, userId).catch((e) => {
        logger.error(`Failed to decline ${userId} from ${room.roomId}`, e);
      });
    },
    [client, room],
  );

  // Power levels change with state too, and Update re-renders us when they do.
  const canAct = myUserId !== null && room.canInvite(myUserId);

  return {
    requests: snapshot,
    admit: canAct ? admit : undefined,
    decline: canAct ? decline : undefined,
  };
}

function readRequests(room: Room): KnockRequest[] {
  return room.getMembersWithMembership(KnownMembership.Knock).map((member) => ({
    userId: member.userId,
    name: member.name,
    avatarMxcUrl: member.getMxcAvatarUrl() ?? null,
  }));
}
